import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { useTheme } from "styled-components/native";
import { CheckBox, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { SelectionType } from "@components/atoms/Filters/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useDispatch } from "react-redux";
import {
  removeFiltersPayload,
  setFiltersPayload,
  useSearchFiltersSlice,
} from "@redux/slices/searchFilters";
import { SearchFilterEnum, treatmentValues } from "../Filters/utils";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import TextListSection from "../Filters/TextListSection";
import {
  checkboxPreviouslySelected,
  isPreviouslySelected,
} from "../Filters/FilterSection/Utils";
import * as Assets from "./assets";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { CheckBoxSectionProps } from "@components/atoms/CheckBoxSection/types";
import useResetFilter from "../../../hooks/useResetFilter";
import { DeviceEventEmitter, View } from "react-native";
import RadioButton from "../RadioButton";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useTranslation } from "react-i18next";
import { useHideFiltersBasedOnAllowedStockItemsType } from "@redux/slices/globalSlice";
import { arraysEqual } from "@utils/product/productValidators";

const CheckBoxSection = ({
  options = [],
  selectionType = SelectionType.multiple,
  sectionTitle = "",
  payloadName = [],
  isRoundCheckbox,
  sectionName = "",
  direction = "column",
  selectedStyle,
  hasSeparator = false,
  subSectionName = "",
  translation,
  selectedProduct,
  selectedCollectionNames,
  isCollapsible,
}: CheckBoxSectionProps) => {
  if (!options) {
    return;
  }
  const isTreatment = subSectionName === "Treatment";
  const { hideLabGrown, hideDiamond, getPriceMode, disableImageZoom } =
    useUserInfo();
  const { auxPayload: filtersPayload, filters } = useSearchFiltersSlice();
  const typography = useTypographyStore();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const basePayloadKey = Array.isArray(payloadName)
    ? payloadName?.[0]
    : payloadName;
  const baseSectionKey =
    basePayloadKey ||
    subSectionName ||
    sectionTitle ||
    sectionName ||
    "options";
  const safeKey = (value: unknown) =>
    String(value ?? "item")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "");
  const baseTestId = `filters.${
    sectionName?.toLowerCase()?.replaceAll(" ", "-")?.toString() || "section"
  }.${safeKey(baseSectionKey)}.checkbox`;
  const value = checkboxPreviouslySelected(
    payloadName,
    filtersPayload,
    sectionName,
    options
  );
  const isTouched = useRef(false);
  const [startRangeIndex, setStartRangeIndex] = useState<number | null>(null);
  const defaultSelected = useMemo(
    () =>
      options?.filter((option) => {
        if (option?.checked) {
          return option;
        }
      }),
    [options]
  );

  if (useHideFiltersBasedOnAllowedStockItemsType()) {
    if (
      hideLabGrown() &&
      options.find((option) => option?.name === "labgrown")
    ) {
      options = options.filter((option) => option?.name !== "labgrown");
    }
    if (hideDiamond() && options.find((option) => option?.name === "diamond")) {
      options = options.filter((option) => option?.name !== "diamond");
    }
  }

  useEffect(() => {
    if (subSectionName === "Watch Size") {
      const subscription = DeviceEventEmitter.addListener(
        "selectedJewelryType",
        ({ sectionName: name }) => {
          if (name !== "Watches" && subSectionName !== "Watch Size") {
            setSelectedItems([]);
          }
        }
      );

      return () => {
        subscription.remove();
      };
    }
  }, []);

  const selected = useMemo(() => {
    if (!value?.length) return [];

    const selectedKeys = value.flatMap((item) => {
      if (typeof item === "string") return item;
      if (typeof item === "object" && item !== null) {
        return Object.keys(item);
      }
      return [];
    });

    return options?.filter((option) => {
      const searchPayload = option?.search_payload?.[0];
      const mPayloadName = option?.payload_name?.[0];

      return (
        selectedKeys.includes(searchPayload) ||
        selectedKeys.includes(mPayloadName)
      );
    });
  }, [options, value]);

  const actualSelected =
    value === null || (Array.isArray(value) && value.length === 0)
      ? defaultSelected
      : selected;

  const [selectedItems, setSelectedItems] = useState<any[]>(
    actualSelected || []
  );

  const isItemSelected = useCallback(
    (item: any) => {
      const itemKey =
        item?.search_payload?.[0] ?? item?.payload_name?.[0] ?? item?.name;
      return selectedItems.some((s) => {
        const sKey = s?.search_payload?.[0] ?? s?.payload_name?.[0] ?? s?.name;
        return sKey !== undefined && sKey === itemKey;
      });
    },
    [selectedItems]
  );

  useEffect(() => {
    if (!isTouched.current && theme.isLargeScreen) {
      const newValue = checkboxPreviouslySelected(
        payloadName,
        filtersPayload,
        sectionName,
        options
      );

      const selectedKeys = (Array.isArray(newValue) ? newValue : []).flatMap(
        (item) => {
          if (typeof item === "string") return item;
          if (typeof item === "object" && item !== null)
            return Object.keys(item);
          return [];
        }
      );

      const newSelected = options?.filter((option) => {
        return (
          selectedKeys.includes(option?.search_payload?.[0]) ||
          selectedKeys.includes(option?.payload_name?.[0])
        );
      });

      const newActualSelected =
        newValue === null || (Array.isArray(newValue) && newValue.length === 0)
          ? defaultSelected
          : newSelected;

      setSelectedItems(newActualSelected || []);
    }
  }, [selectedCollectionNames, filtersPayload]);

  useResetFilter(() => {
    if (isTreatment && defaultSelected) {
      setSelectedItems(defaultSelected);
    } else {
      setSelectedItems([]);
    }
  });

  const renderItemIcon = useCallback(
    (icon?: { type?: string; data?: string }) => {
      if (!icon?.type || !icon?.data) return null;

      switch (icon.type) {
        case "font":
          return (
            <VDBIcon
              variant={IconsVariant.VDBCustom}
              name={icon.data}
              size={24}
            />
          );
        case "url":
          return (
            <VDBIcon
              name={undefined}
              variant={IconsVariant.CustomUrl}
              uri={icon.data}
              size={24}
            />
          );
        case "img":
          return (
            <Image
              resizeMode={disableImageZoom() ? "contain" : "cover"}
              source={Assets[icon.data]}
            />
          );
        default:
          return null;
      }
    },
    []
  );

  const enhancements = useMemo(() => {
    if (filtersPayload?.Pairs?.pair) {
      return filtersPayload?.Pairs[SearchFilterEnum.enhancements] ?? [];
    } else if (
      filtersPayload[SearchFilterEnum.single_stones] &&
      filtersPayload[SearchFilterEnum.single_stones][
        SearchFilterEnum.enhancements
      ]
    ) {
      return (
        filtersPayload[SearchFilterEnum.single_stones][
          SearchFilterEnum.enhancements
        ] ?? []
      );
    }
    return [];
  }, [filtersPayload]);

  const treatmentValuesCheckboxes = useMemo(
    () =>
      options
        ?.map((option) => {
          if (
            option?.search_payload?.[0] &&
            treatmentValues.includes(option?.search_payload?.[0])
          ) {
            return option?.search_payload?.[0];
          }
        })
        .filter((_mValue) => _mValue !== undefined),
    [options]
  );

  const getSelectionValues = (item: any) => {
    return item?.selection_values
      ? item?.selection_values
      : filters?.[selectedProduct]
          ?.find((section: any) => section?.name === sectionName)
          ?.filters?.find(
            (filter: any) =>
              Array.isArray(filter?.payload_name) &&
              filter.payload_name.includes(item?.selection_key)
          )
          ?.options?.filter(
            (option: any) =>
              Array.isArray(option?.price_modes) &&
              option.price_modes.includes(getPriceMode()) &&
              Array.isArray(option?.search_payload)
          )
          .map((filter: any) => filter?.search_payload?.[0]);
  };

  const handleValuesSelection = (item: any) => {
    if (!item?.selection_key) return;

    if (
      selectedItems.findIndex(
        (i) => i.payload_name?.[0] === item.payload_name?.[0]
      ) !== -1
    ) {
      dispatch(
        removeFiltersPayload({
          sectionName,
          payloadName: [item?.selection_key],
        })
      );
    } else {
      const selection_values = getSelectionValues(item);
      const data = {
        [item?.selection_key]: selection_values,
      };
      dispatch(setFiltersPayload({ sectionName, data }));
    }
  };

  useEffect(() => {
    let isChanged = false;
    let currentlySelected = [...selectedItems];
    options.forEach((item) => {
      if (item?.selection_key) {
        const isSelected = currentlySelected.findIndex(
          (i) => i.payload_name?.[0] === item.payload_name?.[0]
        );
        const selection_values = getSelectionValues(item);
        const areArraysEqual = arraysEqual(
          filtersPayload?.[sectionName]?.[item.selection_key],
          selection_values
        );

        if (isSelected !== -1 && !areArraysEqual) {
          currentlySelected = currentlySelected.filter(
            (_item) =>
              !_item.payload_name.some((name: string) =>
                item?.payload_name.includes(name)
              )
          );
          isChanged = true;
        } else if (isSelected === -1 && areArraysEqual) {
          currentlySelected.push(item);
          isChanged = true;
        }
      }
    });
    if (isChanged) {
      setSelectedItems(currentlySelected);
      isTouched.current = true;
    }
  }, [JSON.stringify(filtersPayload?.[sectionName])]);

  const handleItemSelection = useCallback(
    (item: any, index: number) => {
      isTouched.current = true;
      if (item.selection_key) {
        handleValuesSelection(item);
      }
      if (selectionType === SelectionType.single) {
        if (isRoundCheckbox) {
          setSelectedItems([item]);
        } else {
          setSelectedItems((prev) => (prev.includes(item) ? [] : [item]));
        }
      } else if (selectionType === SelectionType.multiple) {
        setSelectedItems((prev) =>
          prev.includes(item)
            ? prev.filter((prevItem) => prevItem !== item)
            : [...prev, item]
        );
      } else if (selectionType === SelectionType.range) {
        if (startRangeIndex === null) {
          setStartRangeIndex(index);
          setSelectedItems([item]);
        } else {
          const minIndex = Math.min(startRangeIndex, index);
          const maxIndex = Math.max(startRangeIndex, index);
          setSelectedItems(options?.slice(minIndex, maxIndex + 1));
          setStartRangeIndex(null);
        }
      }
    },
    [selectionType, options, startRangeIndex, selectedItems]
  );

  useEffect(() => {
    if (
      !(
        (value === null || (Array.isArray(value) && value.length === 0)) &&
        defaultSelected?.length
      ) &&
      !isTouched.current
    )
      return;

    const payload: any = {};
    const booleanCheckBoxes = selectedItems.filter(
      (item) => !item?.search_payload
    );
    const valueCheckBoxes = selectedItems.filter(
      (item) => !!item?.search_payload
    );

    booleanCheckBoxes?.forEach(
      (item) => (payload[item?.payload_name?.[0]] = "true")
    );

    if (payloadName && valueCheckBoxes.length === 0) {
      const shouldProcessEnhancements =
        enhancements &&
        enhancements.length > 0 &&
        payloadName[0] === SearchFilterEnum.enhancements;

      if (shouldProcessEnhancements) {
        const xEnhancements = enhancements?.filter(
          (_value: string) => !treatmentValuesCheckboxes.includes(_value)
        );

        if (xEnhancements.length === 0) {
          dispatch(removeFiltersPayload({ sectionName, payloadName }));
        } else {
          payload[payloadName[0]] = xEnhancements;
        }
      } else {
        dispatch(removeFiltersPayload({ sectionName, payloadName }));
      }
    }

    if (!payloadName && valueCheckBoxes.length === 0) {
      const values = options
        .filter((item) => !!item?.search_payload)
        .map((item) => item.payload_name?.[0]);

      dispatch(removeFiltersPayload({ sectionName, payloadName: values }));
    }

    if (payloadName && valueCheckBoxes?.length) {
      let mParams: string[] = [];
      payloadName?.forEach((name) => {
        const params = valueCheckBoxes
          .map((item) => item.search_payload?.[0])
          .flat();

        let paramsAlreadyAdded = false;
        for (let k = 0; k < params.length; k++) {
          paramsAlreadyAdded = enhancements.includes(params[k]);
        }
        paramsAlreadyAdded =
          paramsAlreadyAdded &&
          params.length === treatmentValuesCheckboxes.length;

        if (
          payloadName[0] === SearchFilterEnum.enhancements &&
          enhancements &&
          !paramsAlreadyAdded
        ) {
          if (params) {
            for (let i = 0; i < params.length; i++) {
              const param = params[i];

              if (!enhancements.includes(param)) {
                mParams = [...enhancements, param];
              } else {
                const filteredValues = treatmentValuesCheckboxes.filter(
                  (_value) => _value !== param
                );

                if (filteredValues.length > 0) {
                  mParams = enhancements.filter(
                    (_xVal: string) => !filteredValues.includes(_xVal)
                  );
                }
              }
            }
          }
        } else {
          mParams = paramsAlreadyAdded ? enhancements : [...params];
        }
        payload[name] =
          selectionType === SelectionType.single ? mParams[0] : mParams;
      });
    } else {
      valueCheckBoxes?.forEach((item) => {
        if (item?.payload_name?.[0] && item?.search_payload?.[0])
          payload[item?.payload_name?.[0]] = item?.search_payload?.[0];
      });
    }

    const removedBooleanCheckBoxes = options.filter(
      (option) =>
        !selectedItems.includes(option) &&
        !option?.search_payload &&
        option?.payload_name?.[0]
    );

    if (removedBooleanCheckBoxes.length > 0) {
      removedBooleanCheckBoxes?.forEach((item) => {
        dispatch(
          removeFiltersPayload({
            sectionName,
            payloadName: [item.payload_name[0]],
          })
        );
      });
    }

    if (sectionName) {
      dispatch(setFiltersPayload({ sectionName, data: payload }));
    }
  }, [selectedItems, payloadName]);

  return (
    <TopLevelView>
      {sectionTitle && (
        <>
          {!isCollapsible && (
            <ParentContainer>
              <VDBText
                isBold
                type={typography["H3-M"]}
                color={theme["core-05"]}
              >
                {sectionTitle}
              </VDBText>
            </ParentContainer>
          )}
          {hasSeparator && (
            <>
              <LineViewContainer background="core-03" />
              <Spacer size={SpaceType.Extra} />
            </>
          )}
        </>
      )}
      <CheckBoxParentContainer direction={direction}>
        {options.map((item, index) => {
          const optionKey = safeKey(
            item?.payload_name?.[0] ||
              item?.search_payload?.[0] ||
              item?.name ||
              item?.label ||
              index
          );
          const optionTestId = `${baseTestId}.option.${optionKey}`;
          return (
            <React.Fragment key={index}>
              <CheckboxChildView
                direction={direction}
                onPress={() => handleItemSelection(item, index)}
                testID={optionTestId}
                data-testId={optionTestId}
              >
                <ConditionalWrapper
                  condition={direction !== "row"}
                  wrapper={(children) => (
                    <InnerParentContainer
                      index={index}
                      lastitem={options.length - 1 === index}
                      hasSeparator={hasSeparator}
                    >
                      {children}
                    </InnerParentContainer>
                  )}
                >
                  <CheckBoxIconView direction={direction}>
                    <CheckBoxDisplay>
                      <View pointerEvents="none">
                        {isRoundCheckbox ? (
                          <RadioButton
                            key={index}
                            checked={isItemSelected(item)}
                            onPress={() => {}}
                            margin="0"
                          />
                        ) : (
                          <CheckBox
                            key={index}
                            accessibilityValue={item.name}
                            isChecked={isItemSelected(item)}
                            checkBoxSize={20}
                          />
                        )}
                      </View>

                      <VDBText
                        type={
                          isItemSelected(item)
                            ? typography["Minor-M"]
                            : typography["Minor-R"]
                        }
                        color={
                          isItemSelected(item)
                            ? theme["core-05"]
                            : theme["core-06"]
                        }
                      >
                        {t(item?.label) || item?.name}
                      </VDBText>
                    </CheckBoxDisplay>

                    {renderItemIcon(item.icon)}
                  </CheckBoxIconView>

                  {item.sub_options?.filter((option) =>
                    option?.price_modes?.includes(getPriceMode())
                  )?.length && isItemSelected(item) ? (
                    <>
                      <TextListSpacer />
                      <TextListSection
                        options={item.sub_options.filter((option) =>
                          option?.price_modes?.includes(getPriceMode())
                        )}
                        columns={item?.columns}
                        selectionType={item?.selection_type}
                        displayedRows={item?.max_row || 0}
                        sectionTitle={item?.name}
                        payloadName={item?.search_payload}
                        parentPayloadName={payloadName}
                        value={isPreviouslySelected(
                          item?.search_payload,
                          filtersPayload,
                          item?.type,
                          sectionName,
                          item?.selection_type
                        )}
                        sectionName={sectionName}
                        selectedStyle={selectedStyle}
                        isSubOption={true}
                        translation={translation}
                      />
                    </>
                  ) : null}
                </ConditionalWrapper>

                {hasSeparator && index !== options.length - 1 && (
                  <LineViewContainer background="core-03" />
                )}
              </CheckboxChildView>
            </React.Fragment>
          );
        })}
      </CheckBoxParentContainer>
      {sectionTitle && <Spacer size={SpaceType.ExtraSmall} />}
    </TopLevelView>
  );
};

const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: (children: React.ReactNode) => React.ReactNode;
  children: React.ReactNode;
}) => (condition ? wrapper(children) : children);

const CheckBoxDisplay = styled.View`
  flex-direction: row;
  align-items: center;
  padding-vertical: 2px;
`;

const CheckBoxParentContainer = styled.View<{
  direction: string;
}>`
  flex-direction: ${({ direction }: { direction: string }) => direction};
  flex-wrap: ${({ direction }: { direction: string }) =>
    direction === "column" ? "wrap" : "no-wrap"};
  width: 100%;
  flex-grow: 1;
  padding-bottom: ${({ direction }: { direction: string }) =>
    direction === "column" ? "0px" : `${SpaceType.Extra}px`};
`;

const CheckboxChildView = styled.Pressable<{ direction: string }>`
  width: ${({ direction }: { direction: string }) =>
    direction === "column" ? "100%" : "auto"};
  ${({ direction }: { direction: string }) =>
    direction === "column" ? "flex-grow: 1" : ""};
  margin-right: ${({ direction }: { direction: string }) =>
    direction === "row" ? `${SpaceType.Double}px` : `0px`};
`;

const InnerParentContainer = styled.View<any>`
  padding-top: ${({
    index,
    hasSeparator,
  }: {
    index: number;
    hasSeparator: boolean;
  }) =>
    index === 0
      ? `0px`
      : `${hasSeparator ? SpaceType.Extra : SpaceType.Basic}px`};
  padding-bottom: ${({
    lastitem,
    hasSeparator,
  }: {
    lastitem: boolean;
    hasSeparator: boolean;
  }) =>
    lastitem
      ? `${hasSeparator ? SpaceType.Extra : SpaceType.Basic}px`
      : `${hasSeparator ? SpaceType.Extra : SpaceType.Basic}px`};
`;

const TextListSpacer = styled.View`
  margin-top: ${SpaceType.Extra}px;
`;

const CheckBoxIconView = styled.View<{
  direction: string;
}>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  width: ${({ direction }: { direction: string }) =>
    direction === "column" ? "100%" : "auto"};
`;

const Image = styled.Image`
  width: 24px;
  height: 24px;
`;

const ParentContainer = styled.View`
  padding: 14.5px ${SpaceType.Double}px;
`;

const TopLevelView = styled.View``;

export default memo(CheckBoxSection);
